import React, { useState } from 'react';

import Form from 'react-jsonschema-form';
import { useHistory } from 'react-router-dom';

import { Button, Stack } from '@mui/material';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import nullsToEmptyStrings from '~/utils/nullsToEmptyStrings';
import { reportError } from '~/utils/reportError';

import { DeleteButton } from '../DeleteButton';
import ErrorDisplay from '../ErrorDisplay';
import PropertyMap from '../PropertyMap';

import ImageUploadField from './ImageUploadField';
import { buttonMessages, buttonStates } from './states/submitButton';
import MarkdownEditor from './widgets/MarkdownEditor';

let uiSchema = {
  id_salesforce_external: { 'ui:widget': 'hidden' },
  itinerary: { 'ui:widget': MarkdownEditor },
  logo_id: {
    'ui:title': 'Tour logo',
    'ui:widget': (props) => (
      <ImageUploadField
        label="logo"
        field_key={props.id}
        value={props.value}
        onUpload={(field_key, newValue) => {
          props.onChange(newValue);
        }}
      />
    ),
  },
  geo_data: {
    classNames: 'row',
    country: {
      classNames: 'col-sm-6 col-md-3',
    },
    country_code: {
      classNames: 'col-sm-6 col-md-3',
    },
    administrative_area_level_1: {
      classNames: 'col-sm-6 col-md-3',
    },
    administrative_area_level_2: {
      classNames: 'col-sm-6 col-md-3',
    },
    administrative_area_level_3: {
      classNames: 'col-sm-6 col-md-3',
    },
    administrative_area_level_4: {
      classNames: 'col-sm-6 col-md-3',
    },
    administrative_area_level_5: {
      classNames: 'col-sm-6 col-md-3',
    },
    locality: {
      classNames: 'col-sm-6 col-md-3',
    },
    route: {
      classNames: 'col-sm-6 col-md-3',
    },
    street_number: {
      classNames: 'col-sm-6 col-md-3',
    },
    place_id: {
      classNames: 'col-sm-6 col-md-3',
    },
  },
};

const errorMessage = `This tour is currently attached to an offer. You must
  detach it from any offers before you can delete.`;

function TourForm({ tour: initialTour, vendorId, schema }) {
  const history = useHistory();

  const [tour, setTour] = useState(nullsToEmptyStrings(initialTour));
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [deleteState, setDeleteState] = useState('delete');

  const onSubmit = (form) => {
    form.errors = [];
    form.errorSchema = {};

    setSaveState(buttonStates.saving);
    setTour(form.formData);

    let submitType = 'createTour';
    let id = null;
    if (tour.id) {
      submitType = 'updateTour';
      id = tour.id;
    }

    ReservationService[submitType](form.formData, id)
      .then((response) => {
        history.push(`/vendors/${vendorId}/tours/${response.id}`);
      })
      .catch(function (e) {
        setSaveState(buttonStates.failed);
        reportError(e);
      });
  };

  const onChange = (edit) => {
    setTour(edit.formData);

    if (saveState !== buttonStates.default) {
      setSaveState(buttonStates.default);
    }
  };

  const onDelete = async () => {
    setDeleteState('deleting');

    const tourIsUsed = await findUsage();

    if (tourIsUsed) {
      setDeleteState('failed');
      return false;
    }

    ReservationService.deleteTour(tour.id)
      .then(function () {
        history.push(`/vendors/${vendorId}`);
      })
      .catch(function (e) {
        if (e.status === 400 && e.errors) {
          alert(e.errors[0].message);
        }
        reportError(e);
      });
  };

  const onMapClick = (latitude, longitude) => {
    // TODO: save the place ID (future enhancement)

    const edit = {
      formData: Object.assign({}, tour, {
        latitude,
        longitude,
      }),
    };
    onChange(edit);
  };

  const onReverseGeocode = (result) => {
    const edit = {
      formData: Object.assign({}, tour, {
        geo_data: result,
      }),
    };
    onChange(edit);
  };

  const findUsage = async () => {
    const offers = await fetchData();

    const typeOffers = offers.filter((offer) => {
      return offer.packages.some((p) => p.fk_tour_id == tour.id);
    });

    return typeOffers.length;
  };

  const fetchData = async () => {
    const { result: offers } = await OffersService.getOffers({
      queryString: vendorId,
    }).catch(function (e) {
      reportError(e);
    });

    return offers;
  };

  return (
    <div>
      <Form schema={schema} onChange={onChange} formData={tour} onSubmit={onSubmit} uiSchema={uiSchema}>
        <label>Select location</label>
        <PropertyMap
          lat={tour.latitude}
          lng={tour.longitude}
          onMapClick={onMapClick}
          reverseGeocode
          onReverseGeocode={onReverseGeocode}
        />
        <div className="button-container">
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" className={saveState}>
              {buttonMessages[saveState]}
            </Button>
            {tour.id ? <DeleteButton onClick={onDelete} buttonText={deleteState} /> : null}
          </Stack>
        </div>

        {deleteState == 'failed' ? <ErrorDisplay message={errorMessage} /> : null}
      </Form>
    </div>
  );
}

export default TourForm;
